


















import Vue from 'vue';
import CloseIcon from '~/assets/svg/close.vue.svg';
import type { ModalEventParams } from '@/plugins/modal/types';

export default Vue.extend({
  name: 'Modal',
  components: {
    CloseIcon,
  },
  props: {
    /**
     * Class for root wrapper
     * `class` is reserved keyword in Vue
     */
    className: {
      type: String,
      default: null,
    },
    name: {
      type: String,
      required: true,
    },
    custom: {
      type: Boolean,
      default: false,
    },
    compact: {
      type: Boolean,
      default: false,
    },
    /**
     * Lock the scroll when modal is open
     */
    scrollLock: {
      type: Boolean,
      default: true,
    },
    /**
     * Clicking outside modal will not close it
     */
    persistent: {
      type: Boolean,
      default: false,
    },
    /**
     * Show modal on a page bottom
     */
    bottomScreen: {
      type: Boolean,
      default: false,
    },
    /**
     * Controls if the first input in the modal should be autofocused
     */
    autoFocusable: {
      type: Boolean,
      default: true,
    },
    /**
     * Close modal window without emitting any events
     */
    closeSilently: {
      type: Boolean,
      default: false,
    },
    onClose: {
      type: Function,
      default: () => {},
    },
    transparent: {
      type: Boolean,
      default: false,
    },
  },
  data: () =>
    ({
      visible: false,
      // Params passed to modal by event (show/hide/toggle)
      params: {},
    } as {
      visible: boolean;
      params: ModalEventParams;
    }),
  computed: {
    // Helper for cypress
    childComponentName() {
      // @ts-ignore
      const match = (this.$parent?.$options?.__file || '').match(/\/?(\w+).vue/);
      return match ? match[1] : '';
    },
    isPersistent() {
      return this.persistent || this.params.persistent || false;
    },
    rootClass() {
      return {
        'modal--transparent': this.transparent || this.params.transparent,
        // Modal shows on a bottom of page PGE-113
        'modal--bottom': this.bottomScreen,
        [this.className]: this.className,
      };
    },
  },
  watch: {
    visible(newValue) {
      if (this.autoFocusable && newValue) {
        this.$nextTick(() => {
          const elemToFocus = (this.$refs.modalRef as HTMLElement)?.querySelector(
            'input, textarea',
          ) as HTMLInputElement;
          if (elemToFocus) elemToFocus.focus();
        });
      }

      if (!newValue && this.$parent.$options.data && !this.$parent.$data.skipDataResetOnModalClose) {
        // @ts-ignore
        Object.assign(this.$parent.$data, this.$parent.$options.data.apply(this.$parent));
      }
    },
  },
  beforeMount() {
    this.$modal.emitter.$on('toggle', this.handleToggleEvent);
    // FIXME: we add a global event and fire it for every modal (even if they are not visible)
    //        it must be only for opened modal
    window.addEventListener('keyup', this.handleEscapeKeyUp);

    // in case modal has been mounted after handler fired
    if (this.name === this.$modal.current) {
      this.$modal.show(this.name, this.$route.query);
    }
  },
  beforeDestroy() {
    // call of the handleToggle func needed when rendering of Modal is conditional
    this.handleToggle(false, this.params);
    this.$modal.emitter.$off('toggle', this.handleToggleEvent);
    window.removeEventListener('keyup', this.handleEscapeKeyUp);

    if (this.scrollLock && window.document.body.hasAttribute('locked')) {
      window.document.body.removeAttribute('locked');
    }
  },
  methods: {
    handleClose() {
      this.handleToggle(false, this.params);
    },

    handleEscapeKeyUp(event: KeyboardEvent) {
      if (event.which === 27 && this.visible && !this.isPersistent) {
        this.handleClose();
      }
    },

    handleOutsideClick(event: KeyboardEvent) {
      if (!this.visible || this.isPersistent) return;

      // FIXME: rewrite to ref, dont rely on class name
      if (document.querySelector('.modal__container') === event.target) {
        this.handleClose();
      }
    },

    handleToggleEvent(name: string, state: boolean, params: ModalEventParams) {
      if (name === this.name) {
        this.handleToggle(state, params);
      }
    },

    handleToggle(state: boolean, params: ModalEventParams = {}) {
      if (params.onClose && !state) params.onClose();
      if (this.visible === state) return;

      const event = state ? 'before-open' : 'before-close';
      const emitEvent = {
        event,
        params,
        name: this.name,
      };

      if (this.scrollLock) {
        const body = window.document.body;
        state ? body.setAttribute('locked', 'true') : body.removeAttribute('locked');
      }

      this.visible = state;
      this.params = params;

      this.$emit(event, emitEvent);

      if (params.silent || this.closeSilently) return;

      this.$modal.emitter.$emit(event, emitEvent);
    },
  },
});
